import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import AddIcon from '@mui/icons-material/Add';
import BackspaceIcon from '@mui/icons-material/Backspace';
import DeleteIcon from '@mui/icons-material/Delete';
import Toast from '../../common/Toast';
import { useToast } from '../../hooks/useToast';
import { resolveWorkforceScope } from './workforceBusinessUnitUtils';
import {
  DEFAULT_WEEK_END_DAY,
  DEFAULT_WEEK_START_DAY,
  buildWorkWeekDays,
  fetchTenantWorkWeek,
} from './workforceWeekUtils';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonateCardSx,
  harmonateDataGridSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogFormContentSx,
  harmonateFormFieldsSx,
  harmonateDrawerAddIconSx,
  harmonateDrawerDeleteIconSx,
  harmonateGridActionIconSx,
  harmonateInputLabelProps,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';

const dayConfig = [
  { key: 'mon', label: 'Monday' },
  { key: 'tue', label: 'Tuesday' },
  { key: 'wed', label: 'Wednesday' },
  { key: 'thu', label: 'Thursday' },
  { key: 'fri', label: 'Friday' },
  { key: 'sat', label: 'Saturday' },
  { key: 'sun', label: 'Sunday' },
];

const dayKeys = dayConfig.map((d) => d.key);

const createDefaultForm = (tenantId, propertyId, username) => ({
  coverageId: '',
  tenantId: tenantId || '',
  propertyId: propertyId || '',
  scheduleId: '',
  rolePositionId: '',
  roleName: '',
  shiftId: '',
  shiftCode: '',
  shiftName: '',
  monStartTime: '',
  monEndTime: '',
  monRequired: 0,
  monAssigned: 0,
  tueStartTime: '',
  tueEndTime: '',
  tueRequired: 0,
  tueAssigned: 0,
  wedStartTime: '',
  wedEndTime: '',
  wedRequired: 0,
  wedAssigned: 0,
  thuStartTime: '',
  thuEndTime: '',
  thuRequired: 0,
  thuAssigned: 0,
  friStartTime: '',
  friEndTime: '',
  friRequired: 0,
  friAssigned: 0,
  satStartTime: '',
  satEndTime: '',
  satRequired: 0,
  satAssigned: 0,
  sunStartTime: '',
  sunEndTime: '',
  sunRequired: 0,
  sunAssigned: 0,
  notes: '',
  createdBy: username || '',
  updatedBy: username || '',
});

const normalizeApiTimeToInput = (value) => {
  if (!value || typeof value !== 'string') return '';
  return value.slice(0, 5);
};

const normalizeInputToApiTime = (value) => {
  if (!value || typeof value !== 'string') return null;
  return value.length === 5 ? `${value}:00` : value;
};

const formatDisplayTime = (value) => {
  if (!value || typeof value !== 'string') return '-';
  const [h, m] = value.split(':');
  if (h == null || m == null) return value;
  const hour = Number(h);
  if (Number.isNaN(hour)) return value;
  const suffix = hour >= 12 ? 'PM' : 'AM';
  const normalized = hour % 12 || 12;
  return `${String(normalized).padStart(2, '0')}:${m} ${suffix}`;
};

const buildDayCellText = (row, key) => {
  const start = row[`${key}StartTime`];
  const end = row[`${key}EndTime`];
  const required = row[`${key}Required`] ?? 0;
  if (!start && !end) return '-';
//   return `${formatDisplayTime(start)} - ${formatDisplayTime(end)} | Req: ${required}`;
  return `${formatDisplayTime(start)} - ${formatDisplayTime(end)}`;
};

const WeeklyRoleCoverage = forwardRef(({
  onBack,
  selectedBusinessUnit,
  selectedProperty,
  selectedRolePosition,
  embedded = false,
  accordionEmbedded = false,
  hideToolbar = false,
  onSelectionChange,
}, ref) => {
  const { canAdd, canEdit, canDelete } = useFunctionPermissions(FC.WORKFORCE_OPTIMIZATION);
  const { toastState, showSuccess, showError, hideToast } = useToast();
  const tenantId = localStorage.getItem('tenant_id') || '';
  const username = localStorage.getItem('username') || '';
  const token = localStorage.getItem('token') || '';
  const [weekStartDay, setWeekStartDay] = useState(DEFAULT_WEEK_START_DAY);
  const [weekEndDay, setWeekEndDay] = useState(DEFAULT_WEEK_END_DAY);
  const visibleDayConfig = useMemo(
    () => buildWorkWeekDays(weekStartDay, weekEndDay).days.map((day) => ({ key: day.key, label: day.label })),
    [weekEndDay, weekStartDay]
  );

  useEffect(() => {
    let cancelled = false;
    const loadWorkWeek = async () => {
      if (!tenantId) return;
      try {
        const week = await fetchTenantWorkWeek(process.env.REACT_APP_BACKEND_URL, token, tenantId);
        if (!cancelled) {
          setWeekStartDay(week.weekStartDay);
          setWeekEndDay(week.weekEndDay);
        }
      } catch {
        if (!cancelled) {
          setWeekStartDay(DEFAULT_WEEK_START_DAY);
          setWeekEndDay(DEFAULT_WEEK_END_DAY);
        }
      }
    };
    loadWorkWeek();
    return () => {
      cancelled = true;
    };
  }, [tenantId, token]);
  const scopeBusinessUnit = selectedBusinessUnit || selectedProperty;
  const { propertyId: selectedPropertyId, businessUnitName: selectedBusinessUnitName } =
    resolveWorkforceScope(scopeBusinessUnit);
  const selectedPropertyName = selectedBusinessUnitName;
  const selectedRolePositionId =
    selectedRolePosition?.role_position_uuid || selectedRolePosition?.role_position_id || selectedRolePosition?.id || '';
  const selectedRoleName = selectedRolePosition?.role_name || selectedRolePosition?.roleName || '';

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectionModel, setSelectionModel] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState('add');
  const [form, setForm] = useState(createDefaultForm(tenantId, selectedPropertyId, username));
  const [submitting, setSubmitting] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const selectedRow = useMemo(
    () => rows.find((row) => row.coverageId === selectionModel[0]) || null,
    [rows, selectionModel]
  );

  useEffect(() => {
    if (onSelectionChange) onSelectionChange(selectedRow);
  }, [onSelectionChange, selectedRow]);

  const columns = useMemo(
    () => [
      { field: 'shiftName', headerName: 'Description', minWidth: 220, flex: 1.1, resizable: true },
      ...visibleDayConfig.map((day) => ({
        field: day.key,
        headerName: day.label,
        minWidth: 170,
        flex: 1,
        sortable: false,
        filterable: false,
        resizable: true,
        valueGetter: (params) => buildDayCellText(params.row, day.key),
      })),
    ],
    [visibleDayConfig]
  );

  const loadCoverage = async () => {
    if (!tenantId || !selectedPropertyId) {
      setRows([]);
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/weekly-role-coverage/by-tenant?tenant_id=${encodeURIComponent(tenantId)}&property_id=${encodeURIComponent(selectedPropertyId)}`,
        {
          method: 'GET',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch weekly coverage: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      const allRows = Array.isArray(data) ? data : [];
      const filteredRows = selectedRolePositionId
        ? allRows.filter((item) => item.rolePositionId === selectedRolePositionId)
        : allRows;
      setRows(filteredRows);
      setSelectionModel([]);
    } catch (fetchError) {
      console.error('Error loading weekly role coverage:', fetchError);
      setRows([]);
      showError(fetchError.message || 'Failed to load weekly position coverage');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCoverage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, selectedPropertyId, selectedRolePositionId]);

  const openAddDrawer = () => {
    if (!canAdd) return;
    setDrawerMode('add');
    setForm({
      ...createDefaultForm(tenantId, selectedPropertyId, username),
      rolePositionId: selectedRolePositionId || '',
      roleName: selectedRoleName || '',
    });
    setDrawerOpen(true);
  };

  useImperativeHandle(ref, () => ({
    openAdd: openAddDrawer,
    openDelete: () => {
      if (!canDelete || !selectedRow?.coverageId) return;
      setDeleteOpen(true);
    },
  }), [canDelete, selectedRow, tenantId, selectedPropertyId, selectedRolePositionId, selectedRoleName, username]);

  const openEditDrawer = (row) => {
    const mapped = {
      ...createDefaultForm(tenantId, selectedPropertyId, username),
      ...row,
      monStartTime: normalizeApiTimeToInput(row.monStartTime),
      monEndTime: normalizeApiTimeToInput(row.monEndTime),
      tueStartTime: normalizeApiTimeToInput(row.tueStartTime),
      tueEndTime: normalizeApiTimeToInput(row.tueEndTime),
      wedStartTime: normalizeApiTimeToInput(row.wedStartTime),
      wedEndTime: normalizeApiTimeToInput(row.wedEndTime),
      thuStartTime: normalizeApiTimeToInput(row.thuStartTime),
      thuEndTime: normalizeApiTimeToInput(row.thuEndTime),
      friStartTime: normalizeApiTimeToInput(row.friStartTime),
      friEndTime: normalizeApiTimeToInput(row.friEndTime),
      satStartTime: normalizeApiTimeToInput(row.satStartTime),
      satEndTime: normalizeApiTimeToInput(row.satEndTime),
      sunStartTime: normalizeApiTimeToInput(row.sunStartTime),
      sunEndTime: normalizeApiTimeToInput(row.sunEndTime),
    };
    setDrawerMode('edit');
    setForm(mapped);
    setDrawerOpen(true);
  };

  const handleCellClick = (params) => {
    if (params.field === '__check__') return;
    if (params.field === 'shiftName' || dayKeys.includes(params.field)) {
      openEditDrawer(params.row);
    }
  };

  const updateForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const clearDayFields = (dayKey) => {
    setForm((prev) => ({
      ...prev,
      [`${dayKey}StartTime`]: '',
      [`${dayKey}EndTime`]: '',
      [`${dayKey}Required`]: '',
      [`${dayKey}Assigned`]: '',
    }));
  };

  const handleSave = async () => {
    if (!tenantId || !selectedPropertyId) {
      showError('Please select a business unit first.');
      return;
    }
    if (!form.rolePositionId || !form.roleName) {
      showError('Position ID and Position are required.');
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        tenantId,
        propertyId: selectedPropertyId,
        scheduleId: form.scheduleId || null,
        rolePositionId: form.rolePositionId || selectedRolePositionId || null,
        roleName: form.roleName || null,
        shiftId: form.shiftId || null,
        shiftCode: form.shiftCode || null,
        shiftName: form.shiftName || null,
        monStartTime: normalizeInputToApiTime(form.monStartTime),
        monEndTime: normalizeInputToApiTime(form.monEndTime),
        tueStartTime: normalizeInputToApiTime(form.tueStartTime),
        tueEndTime: normalizeInputToApiTime(form.tueEndTime),
        wedStartTime: normalizeInputToApiTime(form.wedStartTime),
        wedEndTime: normalizeInputToApiTime(form.wedEndTime),
        thuStartTime: normalizeInputToApiTime(form.thuStartTime),
        thuEndTime: normalizeInputToApiTime(form.thuEndTime),
        friStartTime: normalizeInputToApiTime(form.friStartTime),
        friEndTime: normalizeInputToApiTime(form.friEndTime),
        satStartTime: normalizeInputToApiTime(form.satStartTime),
        satEndTime: normalizeInputToApiTime(form.satEndTime),
        sunStartTime: normalizeInputToApiTime(form.sunStartTime),
        sunEndTime: normalizeInputToApiTime(form.sunEndTime),
        monRequired: Number(form.monRequired) || 0,
        monAssigned: Number(form.monAssigned) || 0,
        tueRequired: Number(form.tueRequired) || 0,
        tueAssigned: Number(form.tueAssigned) || 0,
        wedRequired: Number(form.wedRequired) || 0,
        wedAssigned: Number(form.wedAssigned) || 0,
        thuRequired: Number(form.thuRequired) || 0,
        thuAssigned: Number(form.thuAssigned) || 0,
        friRequired: Number(form.friRequired) || 0,
        friAssigned: Number(form.friAssigned) || 0,
        satRequired: Number(form.satRequired) || 0,
        satAssigned: Number(form.satAssigned) || 0,
        sunRequired: Number(form.sunRequired) || 0,
        sunAssigned: Number(form.sunAssigned) || 0,
        notes: form.notes || null,
        createdBy: form.createdBy || username,
        updatedBy: username,
      };

      const isEdit = drawerMode === 'edit' && form.coverageId;
      const url = isEdit
        ? `${process.env.REACT_APP_BACKEND_URL}conductor/weekly-role-coverage/${form.coverageId}`
        : `${process.env.REACT_APP_BACKEND_URL}conductor/weekly-role-coverage`;
      const method = isEdit ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        let errorMessage = `Failed to save weekly coverage: ${response.status}`;
        const responseText = await response.text().catch(() => '');
        if (responseText) {
          try {
            const parsed = JSON.parse(responseText);
            errorMessage =
              parsed?.error ||
              parsed?.message ||
              parsed?.details ||
              responseText;
          } catch {
            errorMessage = responseText;
          }
        }
        throw new Error(errorMessage);
      }

      setDrawerOpen(false);
      showSuccess(drawerMode === 'edit' ? 'Shift coverage updated successfully' : 'Shift coverage saved successfully');
      await loadCoverage();
    } catch (saveError) {
      console.error('Error saving weekly role coverage:', saveError);
      showError(saveError.message || 'Failed to save weekly position coverage');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!canDelete) return;
    if (!selectedRow?.coverageId) return;
    try {
      setDeleteLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/weekly-role-coverage/${selectedRow.coverageId}`,
        {
          method: 'DELETE',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            'Content-Type': 'application/json',
          },
        }
      );
      if (!response.ok && response.status !== 204) {
        let errorMessage = `Failed to delete shift: ${response.status}`;
        const responseText = await response.text().catch(() => '');
        if (responseText) {
          try {
            const parsed = JSON.parse(responseText);
            errorMessage = parsed?.error || parsed?.message || responseText;
          } catch {
            errorMessage = responseText;
          }
        }
        throw new Error(errorMessage);
      }
      setDeleteOpen(false);
      setSelectionModel([]);
      showSuccess('Shift coverage deleted successfully');
      await loadCoverage();
    } catch (deleteError) {
      console.error('Error deleting weekly role coverage:', deleteError);
      showError(deleteError.message || 'Failed to delete shift coverage');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <>
      {!embedded && (
        <Box sx={{ mb: 3 }}>
          <Breadcrumbs
            aria-label="breadcrumb"
            sx={{ fontSize: '0.9rem', '& .MuiBreadcrumbs-separator': { color: ht.white } }}
          >
            <Link
              underline="hover"
              onClick={onBack}
              sx={{
                cursor: 'pointer',
                color: ht.white,
                '&:hover': { color: ht.white },
              }}
            >
              Workforce Operations
            </Link>
            <Typography sx={{ color: ht.white }}>Weekly Position Coverage</Typography>
          </Breadcrumbs>
        </Box>
      )}

      {!hideToolbar ? (
      <Box
        sx={{
          display: 'flex',
          justifyContent: accordionEmbedded ? 'flex-end' : 'space-between',
          alignItems: 'center',
          mb: accordionEmbedded ? 1 : 2,
          px: accordionEmbedded ? 2 : 0,
          pt: accordionEmbedded ? 1 : 0,
        }}
      >
        {!accordionEmbedded && (
          <Box>
            <Typography variant="body2" sx={{ color: embedded ? ht.text : 'rgba(255,255,255,0.85)' }}>
              {selectedBusinessUnitName || 'No business unit selected'}
            </Typography>
            {!!selectedRoleName && (
              <Typography variant="body2" sx={{ color: embedded ? ht.text : 'rgba(255,255,255,0.85)' }}>
                Position: {selectedRoleName}
              </Typography>
            )}
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Add Weekly Coverage">
            <span>
              <IconButton
                sx={accordionEmbedded ? harmonateGridActionIconSx : harmonateDrawerAddIconSx}
                onClick={openAddDrawer}
                disabled={!selectedPropertyId || !canAdd}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={selectedRow ? 'Delete selected shift' : 'Select a shift to delete'}>
            <span>
              <IconButton
                onClick={() => setDeleteOpen(true)}
                disabled={!selectedRow || !canDelete}
                sx={harmonateDrawerDeleteIconSx}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
      ) : null}

      {!accordionEmbedded && !selectedPropertyId && (
        <Typography variant="body2" sx={{ mb: 2, color: embedded ? ht.text : 'rgba(255,255,255,0.85)' }}>
          Select a business unit from Workforce Operations header to manage weekly position coverage.
        </Typography>
      )}

      <Paper
        elevation={0}
        sx={{
          ...(accordionEmbedded
            ? { borderRadius: 0, boxShadow: 'none', bgcolor: ht.white }
            : harmonateCardSx),
          flex: 1,
          overflow: 'hidden',
          mb: accordionEmbedded ? 0 : 3,
          height: accordionEmbedded ? '100%' : 560,
          minHeight: accordionEmbedded ? 0 : undefined,
          mx: accordionEmbedded ? 2 : 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
          <DataGridPro
            rows={rows}
            columns={columns}
            columnResizeMode="onEnd"
            resizable
            getRowId={(row) => row.coverageId}
            loading={loading}
            rowHeight={44}
            onCellClick={handleCellClick}
            checkboxSelection
            disableMultipleRowSelection
            rowSelectionModel={selectionModel}
            onRowSelectionModelChange={(model) => {
              // Single-select only: keep the latest checked row.
              const next = Array.isArray(model) ? model : model?.ids ? Array.from(model.ids) : [];
              setSelectionModel(next.length ? [next[next.length - 1]] : []);
            }}
            disableRowSelectionOnClick
            pagination
            pageSize={100}
            sx={{
              ...harmonateDataGridSx,
              border: 'none',
              flex: 1,
              '& .MuiDataGrid-cell': { cursor: 'pointer' },
              '& .MuiDataGrid-row:hover': { bgcolor: ht.listBg },
              '& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox': {
                display: 'none',
              },
            }}
          />
        </Box>
      </Paper>

      <Dialog
        open={drawerOpen}
        onClose={() => !submitting && setDrawerOpen(false)}
        maxWidth="md"
        fullWidth
        hideBackdrop
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>{drawerMode === 'edit' ? 'Edit Shift Coverage' : 'Add Shift Coverage'}</DialogTitle>
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx, overflow: 'visible' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            overflow: 'visible',
          }}
        >
        <Box sx={{ display: 'flex', gap: 1.5 }}>

          <TextField
            label="Business Unit"
            size="small"
            fullWidth
            value={selectedPropertyName || ''}
            disabled
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Position"
            size="small"
            fullWidth
            value={form.roleName}
            onChange={(e) => updateForm('roleName', e.target.value)}
            required
            disabled={Boolean(selectedRoleName)}
            InputLabelProps={{ shrink: true }}
          />
          </Box>
          {/* <TextField label="Property ID" size="small" value={selectedPropertyId} disabled /> */}
          {/* {drawerMode === 'edit' && <TextField label="Coverage ID" size="small" value={form.coverageId || ''} disabled />} */}
          {/* <TextField
            label="Schedule ID"
            size="small"
            value={form.scheduleId}
            onChange={(e) => updateForm('scheduleId', e.target.value)}
            required
          /> */}
          {/* <TextField
            label="Position ID"
            size="small"
            value={form.rolePositionId}
            onChange={(e) => updateForm('rolePositionId', e.target.value)}
            required
            disabled={Boolean(selectedRolePositionId)}
          /> */}
          
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            {/* <TextField
              label="Shift ID"
              size="small"
              fullWidth
              value={form.shiftId}
              onChange={(e) => updateForm('shiftId', e.target.value)}
            /> */}
            <TextField
              label="Shift Name"
              size="small"
              fullWidth
              value={form.shiftName}
              onChange={(e) => updateForm('shiftName', e.target.value)}
            />
            <TextField
              label="Shift Code"
              size="small"
              fullWidth
              value={form.shiftCode}
              onChange={(e) => updateForm('shiftCode', e.target.value)}
            />
          </Box>
          {visibleDayConfig.map((day) => {
            const startField = `${day.key}StartTime`;
            const endField = `${day.key}EndTime`;
            const requiredField = `${day.key}Required`;
            const assignedField = `${day.key}Assigned`;
            return (
              <Box key={day.key} sx={{ display: 'flex', gap: 1.5 }}>
                <TextField label="Day" size="small" fullWidth value={day.label} disabled />
                <TextField
                  label="Start Time"
                  type="time"
                  size="small"
                  fullWidth
                  value={form[startField] || ''}
                  onChange={(e) => updateForm(startField, e.target.value)}
                  InputLabelProps={{ ...harmonateInputLabelProps, shrink: true }}
                />
                <TextField
                  label="End Time"
                  type="time"
                  size="small"
                  fullWidth
                  value={form[endField] || ''}
                  onChange={(e) => updateForm(endField, e.target.value)}
                  InputLabelProps={{ ...harmonateInputLabelProps, shrink: true }}
                />
                <TextField
                  label="Required"
                  type="number"
                  size="small"
                  fullWidth
                  value={form[requiredField] ?? ''}
                  onChange={(e) => {
                    const { value } = e.target;
                    updateForm(requiredField, value === '' ? '' : Number(value));
                  }}
                />
                {/* <TextField
                  label="Assigned"
                  type="number"
                  size="small"
                  fullWidth
                  value={form[assignedField] ?? ''}
                  onChange={(e) => {
                    const { value } = e.target;
                    updateForm(assignedField, value === '' ? '' : Number(value));
                  }}
                /> */}
                <Tooltip title={`Clear ${day.label}`}>
                  <IconButton
                    size="small"
                    onClick={() => clearDayFields(day.key)}
                    sx={{ ...harmonateGridActionIconSx, alignSelf: 'center' }}
                  >
                    <BackspaceIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            );
          })}
          <TextField
            label="Notes"
            size="small"
            multiline
            minRows={2}
            value={form.notes || ''}
            onChange={(e) => updateForm('notes', e.target.value)}
          />
        </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDrawerOpen(false)} disabled={submitting} variant="outlined" sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={submitting || !selectedPropertyId}
            sx={harmonateProceedButtonSx}
          >
            {submitting ? (drawerMode === 'edit' ? 'Updating...' : 'Saving...') : (drawerMode === 'edit' ? 'Update' : 'Save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => !deleteLoading && setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Delete shift &quot;{selectedRow?.shiftName || 'selected'}&quot;?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleteLoading}>
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Toast
        open={toastState.open}
        onClose={hideToast}
        message={toastState.message}
        severity={toastState.severity}
        title={toastState.title}
        autoHideDuration={toastState.autoHideDuration}
        position={toastState.position}
      />
    </>
  );
});

export default WeeklyRoleCoverage;
